import { IdArrayShrinker, type ShrunkIdArray } from "utils/idArrayShrinker";
import { SelectionSet } from "utils/selectionSet";

/**
 * Compact an id snapshot for the undo/redo stack.
 *
 * Closures must capture the returned `ShrunkIdArray` only — not a SelectionSet
 * — and call {@link restoreUndoIds} when the callback runs. Hide-all /
 * select-all of a dense 2.86M-id scene is ~357 KB bitmap vs ~11 MB
 * `Uint32Array` (or ~23 MB `number[]`) per stack entry, and the 100-entry cap
 * would otherwise retain both undo and redo copies of the expanded arrays.
 *
 * `SelectionSet` / sorted `Uint32Array` are passed through without copying.
 * Plain arrays are copied and sorted (shrinker requires sorted distinct ids).
 */
export function snapshotUndoIds(ids: Iterable<number>): ShrunkIdArray {
    if (ids instanceof SelectionSet) {
        return IdArrayShrinker.shrink(ids.sortedIds);
    }
    if (ids instanceof Uint32Array) {
        return IdArrayShrinker.shrink(ids);
    }
    const arr = Array.isArray(ids) ? ids.slice() : Array.from(ids);
    arr.sort((a, b) => a - b);
    return IdArrayShrinker.shrink(arr);
}

export function restoreUndoIds(snapshot: ShrunkIdArray): SelectionSet {
    return SelectionSet.fromSortedIds(IdArrayShrinker.expand(snapshot));
}
